import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { EmptyState } from "./InventoryHome";
import { ArrowLeftRight, FileText, Printer } from "lucide-react";

/** Transfers tab — history list; click row to open printable transfer note. */
export default function TransfersTab({ sites, refreshTick, onOpenNote }) {
  const [site, setSite] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api
      .get("/inventory/transfers", { params: site ? { site, limit: 200 } : { limit: 200 } })
      .then((r) => !cancel && setRows(r.data || []))
      .catch(() => !cancel && setRows([]))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [site, refreshTick]);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl">
      <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Site filter</span>
          <select
            data-testid="transfers-site-filter"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
          >
            <option value="">All sites</option>
            {sites.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-slate-500">
          {loading ? "Loading…" : `${rows.length} transfer${rows.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-slate-500">Loading transfers…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          testid="transfers-empty"
          icon={ArrowLeftRight}
          title="No transfers recorded"
          body="Cross-site transfers will appear here. Use the Transfer action above to move stock — a printable transfer note will be generated automatically."
        />
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="transfers-list">
          {rows.map((t) => (
            <li
              key={t.transfer_id}
              data-testid={`transfer-row-${t.transfer_id}`}
              className="px-4 py-3.5 hover:bg-slate-50/60 transition-colors flex items-start gap-3"
            >
              <div className="w-9 h-9 rounded-xl bg-olive/10 grid place-items-center text-olive shrink-0">
                <ArrowLeftRight size={14} strokeWidth={1.9} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-slate-900">
                  {t.source_site} → {t.destination_site}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {new Date(t.date).toLocaleString()} · {t.line_items?.length || 0} line
                  {t.line_items?.length === 1 ? "" : "s"} · by {t.initiated_by_name || "—"}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                  ID · {t.transfer_id}
                </div>
              </div>
              <button
                data-testid={`open-note-${t.transfer_id}`}
                onClick={() => onOpenNote(t)}
                className="rounded-full px-3 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 transition-colors shrink-0"
              >
                <FileText size={12} strokeWidth={2} /> Note
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
